import React, { useState, useEffect } from 'react';
import { logo, profileImage, profileAvatar } from './ImageList';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSelector from './LanguageSelector';




export default function Header() {
    const navigate = useNavigate();
    const [isFixed, setIsFixed] = useState(false);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
    const location = useLocation();
    const dropdownRef = React.useRef(null);
    const { t, i18n } = useTranslation();
    const [consumerFirstName, setConsumerFirstName] = useState('');
    const [consumerLastName, setConsumerLastName] = useState('');
    const [consumerProfileImage, setConsumerProfileImage] = useState('');
    const [imageKey, setImageKey] = useState(0);

    // Force re-render when language changes
    const [updateKey, setUpdateKey] = React.useState(0);
    const prevLanguageRef = React.useRef(i18n.language);

    useEffect(() => {
        const handleLanguageChange = (lng) => {
            // Only update if language actually changed
            if (prevLanguageRef.current !== lng) {
                prevLanguageRef.current = lng;
                setUpdateKey(prev => prev + 1);
            }
        };

        // Listen to i18n language change event
        i18n.on('languageChanged', handleLanguageChange);

        // Also listen to custom window event
        const handleWindowLanguageChange = (event) => {
            const newLang = event.detail?.language || i18n.language;
            if (prevLanguageRef.current !== newLang) {
                prevLanguageRef.current = newLang;
                setUpdateKey(prev => prev + 1);
            }
        };
        window.addEventListener('i18nLanguageChanged', handleWindowLanguageChange);

        return () => {
            i18n.off('languageChanged', handleLanguageChange);
            window.removeEventListener('i18nLanguageChanged', handleWindowLanguageChange);
        };
    }, []);

    useEffect(() => {
        const loadProfileData = () => {
            const fname = localStorage.getItem('consumerFirstName') || '';
            const lname = localStorage.getItem('consumerLastName') || '';
            // Priority: profileImage (updated) > consumerProfileImage (from API)
            const profileImage = localStorage.getItem('conProfileImage') || localStorage.getItem('consumerProfileImage') || '';
            setConsumerFirstName(fname);
            setConsumerLastName(lname);
            // Add timestamp to prevent caching and force re-render
            if (profileImage) {
                const imageWithTimestamp = profileImage + (profileImage.includes('?') ? '&' : '?') + 't=' + Date.now();
                setConsumerProfileImage(imageWithTimestamp);
                // Update key to force image reload
                setImageKey(prev => prev + 1);
            } else {
                setConsumerProfileImage('');
                setImageKey(prev => prev + 1);
            }
        };

        loadProfileData();

        // Listen for profile data updates
        const handleProfileDataUpdate = () => {
            console.log('Header: profileDataUpdated event received');
            loadProfileData();
        };
        window.addEventListener('profileDataUpdated', handleProfileDataUpdate);

        const handleScroll = () => {
            if (window.scrollY > 0) {
                setIsFixed(true);
            } else {
                setIsFixed(false);
            }
        };

        window.addEventListener('scroll', handleScroll);

        return () => {
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('profileDataUpdated', handleProfileDataUpdate);
        };
    }, []);


    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
        };

        if (isDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('touchstart', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, [isDropdownOpen]);

    const isActive = (path) => {
        if (path === '/') {
            return location.pathname === path;
        }
        return location.pathname.startsWith(path);
    };

    const toggleDropdown = () => {
        setIsDropdownOpen(!isDropdownOpen);
    };

    const handleLogoutClick = () => {
        setIsDropdownOpen(false);
        setIsLogoutModalOpen(true);
    };

    const handleLogoutConfirm = () => {
        localStorage.removeItem('userID');
        localStorage.removeItem('agentKey');
        setIsLogoutModalOpen(false);
        navigate('/');
    };

    const handleLogoutCancel = () => {
        setIsLogoutModalOpen(false);
    };

    return (
        <>
            <header className={`header ${isFixed ? 'fixed' : ''}`}>
                <div className="container">
                    {/* <!-- Logo --> */}
                    <div className="logo-section">
                        <div className="logo cursor-pointer" onClick={() => navigate('/home')}>
                            <img className="logo-img" src={logo} alt="YES MLS Logo" />
                        </div>

                        {/* <!-- Navigation --> */}
                        <nav className="nav-menu">
                            <div className={`nav-item ${isActive('/home') ? 'active' : ''}`}>
                                <Link to="/home">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22" fill="none">
                                        <path fillRule="evenodd" clipRule="evenodd" d="M18.7723 6.94675L13.8352 2.8646C12.1721 1.48958 9.82778 1.48958 8.1647 2.8646L3.22747 6.94675C2.33796 7.68221 1.83325 8.7973 1.83325 9.96078V16.3267C1.83325 18.3938 3.42289 20.1667 5.49992 20.1667H7.33325C8.34577 20.1667 9.16659 19.3459 9.16659 18.3333V15.3522C9.16659 14.1903 10.0393 13.3455 10.9999 13.3455C11.9605 13.3455 12.8333 14.1903 12.8333 15.3522V18.3333C12.8333 19.3459 13.654 20.1667 14.6666 20.1667H16.4999C18.577 20.1667 20.1666 18.3938 20.1666 16.3267V9.96078C20.1666 8.79731 19.6619 7.68221 18.7723 6.94675Z" fill="#1C4CAF" />
                                    </svg>
                                    <span>{t('home')}</span>
                                </Link>
                            </div>
                            <div className={`nav-item ${isActive('/profile') ? 'active' : ''}`}>
                                <Link to="/profile">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22" fill="none">
                                        <path d="M11.0001 10.8129C13.4925 10.8129 15.513 8.80278 15.513 6.32313C15.513 3.84348 13.4925 1.83333 11.0001 1.83333C8.50777 1.83333 6.4873 3.84348 6.4873 6.32313C6.4873 8.80278 8.50777 10.8129 11.0001 10.8129Z" fill="black" fillOpacity="0.35" />
                                        <path d="M13.3505 12.6837H8.64966C5.92316 12.6837 3.66675 14.9287 3.66675 17.6412C3.66675 18.296 3.9488 18.8573 4.5129 19.1379C5.35905 19.6056 7.2394 20.1667 11.0001 20.1667C14.7608 20.1667 16.6411 19.6056 17.4872 19.1379C17.9573 18.8573 18.3334 18.296 18.3334 17.6412C18.3334 14.8351 16.077 12.6837 13.3505 12.6837Z" fill="black" fillOpacity="0.35" />
                                    </svg>
                                    <span>{t('profile')}</span>
                                </Link>
                            </div>
                            <div className={`nav-item ${isActive('/favorites') ? 'active' : ''}`}>
                                <Link to="/favorites">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22" fill="none">
                                        <path d="M1.83325 8.37568C1.83325 12.8333 5.51773 15.2088 8.21484 17.335C9.16659 18.0853 10.0833 18.7917 10.9999 18.7917C11.9166 18.7917 12.8333 18.0853 13.785 17.335C16.4821 15.2088 20.1666 12.8333 20.1666 8.37568C20.1666 3.91798 15.1247 0.756677 10.9999 5.04225C6.87507 0.756677 1.83325 3.91798 1.83325 8.37568Z" fill="black" fillOpacity="0.35" />
                                    </svg>
                                    <span>{t('favorites')}</span>
                                </Link>
                            </div>
                            <div className={`nav-item ${isActive('/filter') ? 'active' : ''}`}>
                                <Link to="/filter">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22" fill="none">
                                        <path d="M18.3528 2.75H3.64713C3.27381 2.75029 2.9086 2.85894 2.59583 3.06276C2.28305 3.26657 2.03616 3.55679 1.88511 3.89819C1.73406 4.23959 1.68536 4.6175 1.74491 4.98604C1.80445 5.35458 1.96969 5.69792 2.22057 5.97437L8.5937 12.9834V19.5938C8.59375 19.723 8.63022 19.8496 8.69893 19.959C8.76763 20.0684 8.86578 20.1563 8.98213 20.2125C9.07512 20.2584 9.17752 20.2819 9.2812 20.2812C9.43745 20.2811 9.58901 20.2278 9.71089 20.13L10.9999 19.0988L13.1484 17.38C13.2288 17.3156 13.2937 17.234 13.3383 17.1412C13.3829 17.0484 13.4061 16.9467 13.4062 16.8438V12.9834L19.7793 5.97437C20.0302 5.69792 20.1954 5.35458 20.255 4.98604C20.3145 4.6175 20.2658 4.23959 20.1148 3.89819C19.9637 3.55679 19.7168 3.26657 19.4041 3.06276C19.0913 2.85894 18.7261 2.75029 18.3528 2.75Z" fill="black" fillOpacity="0.35" />
                                    </svg>
                                    <span>{t('filter')}</span>
                                </Link>
                            </div>
                        </nav>

                        {/* <!-- User Section --> */}
                        <div className="user-section">
                            <Link to="/notifications" className="notification-icon" aria-label="Notifications">
                                <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 30 30" fill="none">
                                    <path d="M25.786 19.9986C25.6888 19.8814 25.5933 19.7643 25.4995 19.6512C24.2105 18.092 23.4306 17.151 23.4306 12.7371C23.4306 10.452 22.8839 8.57695 21.8064 7.1707C21.0118 6.13184 19.9378 5.34375 18.5222 4.76133C18.504 4.75119 18.4877 4.7379 18.4741 4.72207C17.965 3.01699 16.5716 1.875 15.0001 1.875C13.4286 1.875 12.0359 3.01699 11.5267 4.72031C11.5131 4.73556 11.497 4.74843 11.4792 4.7584C8.1757 6.11836 6.57023 8.72754 6.57023 12.7354C6.57023 17.151 5.79152 18.092 4.50129 19.6494C4.40754 19.7625 4.31203 19.8773 4.21476 19.9969C3.96351 20.2999 3.80433 20.6685 3.75604 21.0592C3.70775 21.4498 3.77239 21.8461 3.9423 22.2012C4.30382 22.9629 5.07433 23.4357 5.95382 23.4357H24.0528C24.9282 23.4357 25.6935 22.9635 26.0562 22.2053C26.2268 21.8501 26.2921 21.4535 26.2442 21.0624C26.1963 20.6713 26.0373 20.3021 25.786 19.9986Z" fill="#1C4CAF" />
                                    <path d="M14.9999 28.125C15.8466 28.1243 16.6773 27.8945 17.404 27.4599C18.1306 27.0253 18.7261 26.4021 19.1272 25.6564C19.1461 25.6207 19.1555 25.5807 19.1543 25.5403C19.1532 25.4999 19.1416 25.4605 19.1208 25.4258C19.0999 25.3912 19.0704 25.3626 19.0352 25.3427C19 25.3229 18.9602 25.3125 18.9198 25.3125H11.0811C11.0407 25.3124 11.0009 25.3227 10.9656 25.3425C10.9303 25.3623 10.9007 25.391 10.8798 25.4256C10.8588 25.4602 10.8472 25.4997 10.846 25.5401C10.8449 25.5806 10.8542 25.6207 10.8731 25.6564C11.2742 26.402 11.8696 27.0251 12.5961 27.4597C13.3227 27.8943 14.1533 28.1242 14.9999 28.125Z" fill="#1C4CAF" />
                                </svg>
                            </Link>

                            <LanguageSelector />
                            <div className="user-info dropdown" ref={dropdownRef}>
                                <div
                                    className="dropdown-toggle"
                                    role="button"
                                    onClick={toggleDropdown}
                                    aria-expanded={isDropdownOpen}
                                >
                                    <img
                                        src={consumerProfileImage || localStorage.getItem('conProfileImage') || localStorage.getItem('consumerProfileImage') || ''}
                                        alt="Profile"
                                        className="profile-img"
                                        key={`profile-img-${imageKey}-${consumerProfileImage ? consumerProfileImage.split('?')[0] : 'default'}`}
                                    />
                                    <span key={`hello-${i18n.language}-${updateKey}`}>{t('hello')}, <br />{localStorage.getItem('consumerFirstName')} {localStorage.getItem('consumerLastName')}</span>
                                </div>
                                <ul className={`dropdown-menu dropdown-menu-end ${isDropdownOpen ? 'show' : ''}`}>
                                    <li><button className="dropdown-item" onClick={handleLogoutClick}>{t('logout')}</button></li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            {/* <!-- Mobile Header --> */}

            {/* Logout Confirmation Modal */}
            {isLogoutModalOpen && (
                <>
                    <div
                        className="modal-overlay"
                        onClick={handleLogoutCancel}

                    />
                    <div
                        className="logout-modal"

                    >
                        <h3 style={{
                            margin: '0 0 15px 0',
                            fontSize: '20px',
                            fontWeight: '600',
                            color: '#fff'
                        }}>
                            {t('confirmLogout') || 'Confirm Logout'}
                        </h3>
                        <p style={{
                            margin: '0 0 25px 0',
                            fontSize: '16px',
                            color: '#fff',
                            lineHeight: '1.5'
                        }}>
                            {t('areYouSureYouWantToLogout') || 'Are you sure you want to logout?'}
                        </p>
                        <div className='btn-sec'>
                            <button className='cancel-btn custom-btn'
                                onClick={handleLogoutCancel}

                            >
                                {t('no') || 'No'}
                            </button>
                            <button className='save-btn custom-btn'
                                onClick={handleLogoutConfirm}

                            >
                                {t('yes') || 'Yes'}
                            </button>
                        </div>
                    </div>
                </>
            )}






        </>
    );
}